import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import UpgradeModal from "@/components/UpgradeModal";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building, Upload, X, Download, Trash2, Lock } from "lucide-react";
import SubscriptionManagement from "@/components/SubscriptionManagement";

export default function Settings() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    owner_name: "",
    address: "",
    street: "",
    street_number: "",
    postal_code: "",
    city: "",
    tax_id: "",
    che_number: "",
    company_register_number: "",
    phone: "",
    email: "",
    website: "",
    bank_name: "",
    iban: "",
    bic: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setFormData({
        company_name: response.data.company_name || "",
        owner_name: response.data.owner_name || "",
        address: response.data.address || "",
        street: response.data.street || "",
        street_number: response.data.street_number || "",
        postal_code: response.data.postal_code || "",
        city: response.data.city || "",
        tax_id: response.data.tax_id || "",
        che_number: response.data.che_number || "",
        company_register_number: response.data.company_register_number || "",
        phone: response.data.phone || "",
        email: response.data.email || "",
        website: response.data.website || "",
        bank_name: response.data.bank_name || "",
        iban: response.data.iban || "",
        bic: response.data.bic || "",
      });
      if (response.data.logo_url) {
        setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload logo if changed
      if (logoFile) {
        const formDataLogo = new FormData();
        formDataLogo.append("file", logoFile);
        await api.post(`/settings/upload-logo`, formDataLogo, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Update settings (country is now managed by CountryInfo component)
      await api.put(`/settings`, formData);
      
      toast.success(t("settings.saved"));
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(t('settings.saveError', 'Error saving'));
    } finally {
      setLoading(false);
    }
  };

  const handleDataExport = async () => {
    try {
      const response = await api.get('/user/export-data');
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ivoliq-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t('settings.dataExported'));
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t('settings.exportError'));
    }
  };

  // Change Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t('settings.fillAllPasswordFields', 'Bitte füllen Sie alle Felder aus'));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('settings.passwordsDontMatch', 'Neue Passwörter stimmen nicht überein'));
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(t('settings.passwordTooShort', 'Neues Passwort muss mindestens 8 Zeichen haben'));
      return;
    }

    try {
      await api.post('/auth/change-password', null, {
        params: {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        }
      });
      toast.success(t('settings.passwordChanged', 'Passwort erfolgreich geändert'));
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      // Logout and redirect to login after password change
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.status === 401) {
        toast.error(t('settings.currentPasswordIncorrect', 'Aktuelles Passwort ist falsch'));
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('OAuth')) {
        toast.error(t('settings.cannotChangeOAuthPassword', 'Passwort kann für OAuth-Accounts nicht geändert werden'));
      } else {
        toast.error(t('settings.passwordChangeError', 'Fehler beim Ändern des Passworts'));
      }
    }
  };

  // Delete Account State
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error(t('settings.passwordRequired', 'Passwort erforderlich'));
      return;
    }

    try {
      await api.post('/auth/delete-account', { password: deletePassword });
      toast.success(t('settings.accountDeleted', 'Account wurde erfolgreich gelöscht'));
      setDeleteDialogOpen(false);
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.response?.status === 401) {
        toast.error(t('settings.invalidPassword', 'Falsches Passwort'));
      } else {
        toast.error(t('settings.deleteError', 'Fehler beim Löschen des Accounts'));
      }
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("settings.title")}</h1>
        <p className="text-lg text-gray-600">{t("settings.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.logo")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="h-24 w-24 object-contain border-2 border-gray-200 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Building className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                    <Upload className="h-4 w-4" />
                    <span>{t("settings.uploadLogo")}</span>
                  </div>
                </Label>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <p className="text-xs text-gray-500 mt-2">
                  PNG, JPG oder SVG (empfohlen: 200x200px)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.companyInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.companyName")}</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Meine Firma GmbH"
                />
              </div>
              <div>
                <Label>{t("settings.ownerName")}</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="Max Mustermann"
                />
              </div>
            </div>

            <div>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">{t('settings.address', 'Address')}</p>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="street">{t('customers.street', 'Street')}</Label>
                    <Input
                      id="street"
                      placeholder={t('customers.streetPlaceholder', 'Street name')}
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="street_number">{t('customers.streetNumber', 'No.')}</Label>
                    <Input
                      id="street_number"
                      placeholder="123"
                      value={formData.street_number}
                      onChange={(e) => setFormData({ ...formData, street_number: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="postal_code">{t('customers.postalCode', 'ZIP')}</Label>
                    <Input
                      id="postal_code"
                      placeholder="12345"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="city">{t('customers.city', 'City')}</Label>
                    <Input
                      id="city"
                      placeholder={t('customers.cityPlaceholder', 'City name')}
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-xs text-yellow-800">
                    ⚠️ <strong>{t('settings.structuredAddressRequired', 'Swiss Compliance:')}</strong> {t('settings.structuredAddressNote', 'Structured address is mandatory for Swiss QR invoices from 21.11.2025. Please enter street and number separately.')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {/* Germany & Austria: Tax ID / USt-IdNr. */}
                {user?.country === "DE" && (
                  <div>
                    <Label>Steuernummer / USt-IdNr.</Label>
                    <Input
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                      placeholder="DE123456789"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ihre Steuernummer oder Umsatzsteuer-Identifikationsnummer (USt-IdNr.)
                    </p>
                  </div>
                )}
                
                {user?.country === "AT" && (
                  <>
                    <div>
                      <Label>UID-Nummer (Umsatzsteuer-Identifikationsnummer)</Label>
                      <Input
                        value={formData.tax_id}
                        onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                        placeholder="ATU12345678"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ihre österreichische UID-Nummer (z.B. ATU12345678)
                      </p>
                    </div>
                    <div>
                      <Label>Firmenbuchnummer (optional)</Label>
                      <Input
                        value={formData.company_register_number}
                        onChange={(e) => setFormData({ ...formData, company_register_number: e.target.value })}
                        placeholder="FN 123456a"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Ihre Firmenbuchnummer beim österreichischen Firmenbuch
                      </p>
                    </div>
                  </>
                )}
                
                {user?.country === "CH" && (
                  <div>
                    <Label>CHE-Nummer (Unternehmens-Identifikationsnummer)</Label>
                    <Input
                      value={formData.che_number}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setFormData({ ...formData, che_number: value });
                      }}
                      placeholder="CHE-123.456.789 MWST"
                      pattern="^CHE-[0-9]{3}\.[0-9]{3}\.[0-9]{3}(\s*(MWST|TVA|IVA))?$"
                      title="Format: CHE-123.456.789 MWST"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ihre CHE-Nummer mit MWST-Zusatz (z.B. CHE-123.456.789 MWST)
                    </p>
                  </div>
                )}
                
                {!user?.country || user?.country === "OTHER" && (
                  <div>
                    <Label>{t("settings.taxId")}</Label>
                    <Input
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                      placeholder="Tax ID / VAT Number"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('settings.taxIdHelper', 'Your tax identification number or VAT number')}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t("settings.phone")}</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+49 123 456789"
                  />
                </div>
                <div>
                  <Label>{t("settings.email")}</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="kontakt@firma.de"
                  />
                </div>
                <div>
                  <Label>{t("settings.website")}</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="www.firma.de"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>{t("settings.website")}</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="www.meinefirma.de"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bank Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.bankInfo")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t("settings.bankName")}</Label>
              <Input
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Beispielbank"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("settings.iban")}</Label>
                <Input
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="DE89 3704 0044 0532 0130 00"
                />
              </div>
              <div>
                <Label>{t("settings.bic")}</Label>
                <Input
                  value={formData.bic}
                  onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
                  placeholder="COBADEFFXXX"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              {t("settings.exportDataTitle")}
            </CardTitle>
            <CardDescription>{t("settings.exportDataDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleDataExport}
              className="w-full"
            >
              {t("settings.exportData")}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-emerald-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-600">
              <Lock className="h-5 w-5" />
              {t("settings.changePasswordTitle", "Change Password")}
            </CardTitle>
            <CardDescription>
              {t("settings.changePasswordDesc", "Change your login password. You will be logged out after the change.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                >
                  {t("settings.changePassword", "Change Password")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("settings.changePasswordTitle", "Change Password")}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {t("settings.changePasswordInfo", "After the change, you will be logged out and must sign in with your new password.")}
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="current-password" className="text-sm font-medium">
                          {t("settings.currentPassword", "Aktuelles Passwort")}
                        </Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                          placeholder={t("settings.enterCurrentPassword", "Aktuelles Passwort eingeben")}
                          className="mt-1"
                          autoComplete="current-password"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-password" className="text-sm font-medium">
                          {t("settings.newPassword", "Neues Passwort")}
                        </Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                          placeholder={t("settings.enterNewPassword", "Neues Passwort (min. 8 Zeichen)")}
                          className="mt-1"
                          autoComplete="new-password"
                          minLength={8}
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirm-password" className="text-sm font-medium">
                          {t("settings.confirmNewPassword", "Confirm New Password")}
                        </Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                          placeholder={t("settings.confirmNewPassword", "Neues Passwort bestätigen")}
                          className="mt-1"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" })}>
                    {t("common.cancel", "Abbrechen")}
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleChangePassword}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  >
                    {t("settings.changePassword", "Change Password")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t("settings.deleteAccountTitle", "Delete Account")}
            </CardTitle>
            <CardDescription>
              {t("settings.deleteAccountDesc", "Permanently delete your account and all associated data.")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button 
                  type="button" 
                  variant="destructive" 
                  className="w-full"
                >
                  {t("settings.deleteAccount", "Delete Account")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("settings.confirmDeleteTitle", "Really delete account?")}
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>
                      {t("settings.confirmDeleteDesc", "This action cannot be undone. The following data will be deleted:")}
                    </p>
                    <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                      <li>{t("settings.deleteData1", "All projects and time entries")}</li>
                      <li>{t("settings.deleteData2", "Alle Kunden und Rechnungen")}</li>
                      <li>{t("settings.deleteData3", "Alle Einnahmen und Ausgaben")}</li>
                      <li>{t("settings.deleteData4", "Your Stripe subscription will be cancelled")}</li>
                    </ul>
                    <div className="pt-3">
                      <Label htmlFor="delete-password" className="text-sm font-medium">
                        {t("settings.confirmPassword", "Password to confirm")}
                      </Label>
                      <Input
                        id="delete-password"
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        placeholder={t("settings.enterPassword", "Passwort eingeben")}
                        className="mt-2"
                        autoComplete="current-password"
                      />
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeletePassword("")}>
                    {t("common.cancel", "Abbrechen")}
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteAccount}
                    className="bg-red-600 hover:bg-red-700"
                    disabled={!deletePassword}
                  >
                    {t("settings.confirmDelete", "Delete Permanently")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        <Button type="submit" className="w-full" disabled={loading} data-testid="save-settings-button">
          {loading ? "Speichert..." : t("common.save")}
        </Button>
      </form>

      {/* Subscription Management - outside form */}
      <SubscriptionManagement />

      {/* Country Information */}
      <CountryInfo />

      {/* Danger Zone - Account Deletion */}
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-700 flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Gefahrenbereich
          </CardTitle>
          <CardDescription className="text-red-600">
            Vorsicht: Diese Aktion kann nicht rückgängig gemacht werden
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full md:w-auto">
                <Trash2 className="h-4 w-4 mr-2" />
                Account dauerhaft löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-600">
                  Account wirklich löschen?
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-3 text-left">
                  <p className="font-semibold text-gray-900">
                    ⚠️ Diese Aktion löscht unwiderruflich:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Alle Ihre Kunden und Projekte</li>
                    <li>Alle Zeiterfassungen</li>
                    <li>Alle Rechnungen und Finanzdaten</li>
                    <li>Ihre Steuerberechnungen</li>
                    <li>Ihr gesamtes Benutzerkonto</li>
                  </ul>
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mt-3">
                    <p className="text-sm text-yellow-900">
                      <strong>Hinweis:</strong> Rechnungsdaten werden aus steuerrechtlichen Gründen für 7 Jahre aufbewahrt, sind aber nicht mehr einsehbar.
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mt-3">
                    Möchten Sie Ihren Account wirklich löschen?
                  </p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    try {
                      await api.delete('/user/account');
                      toast.success('Account wurde gelöscht');
                      logout();
                      navigate('/login');
                    } catch (error) {
                      toast.error('Fehler beim Löschen des Accounts');
                      console.error(error);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Ja, Account unwiderruflich löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}

// New component for Country Information with edit capability
function CountryInfo() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [countryInfo, setCountryInfo] = useState(null);
  const [originalCountryInfo, setOriginalCountryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCanton, setSelectedCanton] = useState('');
  const [saving, setSaving] = useState(false);
  const [countryChangeDialogOpen, setCountryChangeDialogOpen] = useState(false);
  const [pendingCountry, setPendingCountry] = useState('');

  const countries = [
    { code: 'DE', name: t('country.germany', 'Deutschland'), flag: '🇩🇪' },
    { code: 'AT', name: t('country.austria', 'Österreich'), flag: '🇦🇹' },
    { code: 'CH', name: t('country.switzerland', 'Schweiz'), flag: '🇨🇭' },
    { code: 'OTHER', name: t('country.other', 'Anderes Land'), flag: '🌍' }
  ];

  useEffect(() => {
    const fetchCountryInfo = async () => {
      try {
        const response = await api.get('/country/info');
        setCountryInfo(response.data);
        setOriginalCountryInfo(JSON.parse(JSON.stringify(response.data))); // Deep copy
        setSelectedCountry(response.data.country_code);
      } catch (error) {
        console.error("Error fetching country info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountryInfo();
  }, []);

  const handleSave = async () => {
    // Validation for CH: canton must be selected
    if (selectedCountry === 'CH' && !selectedCanton) {
      toast.error(t('settings.cantonRequired', 'Bitte wählen Sie einen Kanton aus'));
      return;
    }
    
    // Validation for DE: if church tax is enabled, bundesland must be selected
    if (selectedCountry === 'DE' && countryInfo?.church_tax && !countryInfo?.bundesland) {
      toast.error(t('settings.bundeslandRequired', 'Bitte wählen Sie ein Bundesland aus'));
      return;
    }
    
    setSaving(true);
    try {
      const params = { country_code: selectedCountry };
      if (selectedCountry === 'CH' && selectedCanton) {
        params.canton = selectedCanton;
      }
      if (selectedCountry === 'DE') {
        params.church_tax = countryInfo?.church_tax || false;
        if (countryInfo?.bundesland) {
          params.bundesland = countryInfo.bundesland;
        }
      }
      if (selectedCountry === 'AT') {
        params.svs_opt_out = countryInfo?.svs_opt_out || false;
      }
      
      await api.put('/country/update', null, { params });
      
      // Refresh country info
      const response = await api.get('/country/info');
      setCountryInfo(response.data);
      setOriginalCountryInfo(JSON.parse(JSON.stringify(response.data))); // Update original after save
      setEditing(false);
      toast.success(t('settings.countryUpdated', 'Steueransässigkeit aktualisiert'));
    } catch (error) {
      console.error("Error updating country:", error);
      toast.error(t('settings.countryUpdateError', 'Fehler beim Aktualisieren'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Restore original values
    if (originalCountryInfo) {
      setCountryInfo(JSON.parse(JSON.stringify(originalCountryInfo)));
      setSelectedCountry(originalCountryInfo.country_code);
      setSelectedCanton(originalCountryInfo.canton || '');
    }
    setEditing(false);
  };

  if (loading) {
    return null;
  }

  if (!countryInfo) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{t("settings.countryInfo")}</CardTitle>
            <CardDescription>{t("settings.countryInfoDesc")}</CardDescription>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t("settings.editCountry", "Edit")}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {editing ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("settings.selectTaxCountry", "Land der Steuerpflicht")}
                </label>
                <div className="space-y-2">
                  {countries.map(country => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => {
                        // If user tries to change country, show warning dialog
                        if (country.code !== countryInfo.country_code) {
                          setPendingCountry(country.code);
                          setCountryChangeDialogOpen(true);
                        } else {
                          setSelectedCountry(country.code);
                        }
                      }}
                      className={`w-full flex items-center justify-between p-3 border-2 rounded-lg transition-all ${
                        selectedCountry === country.code
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">{country.flag}</span>
                        <span className="font-medium text-gray-900">{country.name}</span>
                      </div>
                      {selectedCountry === country.code && (
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Canton selection for Switzerland */}
              {selectedCountry === 'CH' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("settings.selectCanton", "Select Your Canton")}
                  </label>
                  <select
                    value={selectedCanton || ''}
                    onChange={(e) => setSelectedCanton(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t("settings.selectCantonPlaceholder", "Please select...")}</option>
                    <option value="ZH">🇨🇭 Zürich</option>
                    <option value="ZG">🇨🇭 Zug ({t('settings.lowestTaxes', 'Lowest taxes')})</option>
                    <option value="BS">🇨🇭 Basel-Stadt</option>
                    <option value="BL">🇨🇭 Basel-Landschaft</option>
                    <option value="BE">🇨🇭 Bern</option>
                    <option value="GE">🇨🇭 Genf ({t('settings.highestTaxes', 'Highest taxes')})</option>
                    <option value="VD">🇨🇭 Waadt</option>
                    <option value="LU">🇨🇭 Luzern</option>
                    <option value="SG">🇨🇭 St. Gallen</option>
                    <option value="AG">🇨🇭 Aargau</option>
                    <option value="TI">🇨🇭 Tessin</option>
                    <option value="GR">🇨🇭 Graubünden</option>
                  </select>
                  <p className="text-xs text-gray-600 mt-2">
                    {t("settings.cantonNote", "Your canton is required for tax calculation. Tax rates vary significantly between cantons.")}
                  </p>
                </div>
              )}
              
              {/* Church tax settings for Germany */}
              {selectedCountry === 'DE' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="mr-2">⛪</span>
                      {t("settings.churchTax", "Kirchensteuerpflichtig")}
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={countryInfo?.church_tax || false}
                        onChange={(e) => {
                          setCountryInfo({...countryInfo, church_tax: e.target.checked});
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                  
                  {countryInfo?.church_tax && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("settings.bundesland", "Bundesland")}
                      </label>
                      <select
                        value={countryInfo?.bundesland || ''}
                        onChange={(e) => setCountryInfo({...countryInfo, bundesland: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">{t("settings.selectBundesland", "Please select...")}</option>
                        <optgroup label="8% Kirchensteuer">
                          <option value="BW">🇩🇪 Baden-Württemberg (8%)</option>
                          <option value="BY">🇩🇪 Bayern (8%)</option>
                        </optgroup>
                        <optgroup label="9% Kirchensteuer">
                          <option value="BE">🇩🇪 Berlin (9%)</option>
                          <option value="BB">🇩🇪 Brandenburg (9%)</option>
                          <option value="HB">🇩🇪 Bremen (9%)</option>
                          <option value="HH">🇩🇪 Hamburg (9%)</option>
                          <option value="HE">🇩🇪 Hessen (9%)</option>
                          <option value="MV">🇩🇪 Mecklenburg-Vorpommern (9%)</option>
                          <option value="NI">🇩🇪 Niedersachsen (9%)</option>
                          <option value="NW">🇩🇪 Nordrhein-Westfalen (9%)</option>
                          <option value="RP">🇩🇪 Rheinland-Pfalz (9%)</option>
                          <option value="SL">🇩🇪 Saarland (9%)</option>
                          <option value="SN">🇩🇪 Sachsen (9%)</option>
                          <option value="ST">🇩🇪 Sachsen-Anhalt (9%)</option>
                          <option value="SH">🇩🇪 Schleswig-Holstein (9%)</option>
                          <option value="TH">🇩🇪 Thüringen (9%)</option>
                        </optgroup>
                      </select>
                      <p className="text-xs text-gray-600 mt-2">
                        {t("settings.churchTaxNote", "Church tax is 8% (BW, BY) or 9% (other states) of income tax. You can leave the church at the district court/registry office.")}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* SVS Opt-Out settings for Austria */}
              {selectedCountry === 'AT' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="mr-2">🇦🇹</span>
                      {t("settings.svsOptOut", "SVS Opt-Out (Befreiung)")}
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${countryInfo?.svs_opt_out ? 'text-red-600' : 'text-gray-500'}`}>
                        {countryInfo?.svs_opt_out ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      {!countryInfo?.svs_opt_out ? (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              type="button"
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded transition-colors"
                            >
                              Aktivieren
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                                <span className="text-2xl">⚠️</span>
                                SVS Opt-Out Bestätigung erforderlich
                              </AlertDialogTitle>
                              <AlertDialogDescription className="space-y-3 text-left">
                                <p className="font-semibold text-gray-900">
                                  Sie sind dabei, sich von der SVS-Pflichtversicherung zu befreien.
                                </p>
                                <div className="bg-red-100 border border-red-300 p-3 rounded">
                                  <p className="font-bold text-red-900 mb-2">⚠️ Kritische Konsequenzen:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm text-red-900">
                                    <li><strong>Kein Krankenversicherungsschutz</strong></li>
                                    <li><strong>Keine Pensionsversicherung</strong></li>
                                    <li><strong>Keine Unfallversicherung</strong></li>
                                    <li>Sie zahlen keine SVS-Beiträge</li>
                                  </ul>
                                </div>
                                <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                                  <p className="font-semibold text-gray-900 mb-2">Voraussetzungen für Opt-Out:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    <li>Jahresgewinn ≤ 6.613,20 €</li>
                                    <li>Jahresumsatz ≤ 55.000 €</li>
                                    <li>UND: Neugründer ODER 60+ Jahre alt</li>
                                  </ul>
                                </div>
                                <p className="text-sm font-semibold text-gray-900">
                                  Bestätigen Sie, dass Sie die Konsequenzen verstanden haben und die Voraussetzungen erfüllen?
                                </p>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Nein, abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => setCountryInfo({...countryInfo, svs_opt_out: true})}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Ja, ich verstehe - Opt-Out aktivieren
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCountryInfo({...countryInfo, svs_opt_out: false})}
                          className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium rounded transition-colors"
                        >
                          Deaktivieren
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-700 space-y-2">
                    <p className="font-semibold">
                      {t("settings.svsOptOutTitle", "Befreiung von der SVS-Pflichtversicherung")}
                    </p>
                    <p>
                      {t("settings.svsOptOutExplanation", "You can be exempted from SVS if your annual profit is ≤ €6,613.20 and annual revenue is ≤ €55,000.")}
                    </p>
                    <div className="bg-white p-3 rounded border border-red-300">
                      <p className="font-semibold mb-1">Voraussetzungen:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Jahresgewinn max. 6.613,20 €</li>
                        <li>Jahresumsatz max. 55.000 €</li>
                        <li>UND: Neugründer ODER 60+ Jahre alt</li>
                      </ul>
                    </div>
                    <p className="text-red-700 font-semibold">
                      ⚠️ Achtung: Bei Opt-Out haben Sie keinen SVS-Versicherungsschutz (Kranken-, Pensions-, Unfallversicherung)!
                    </p>
                  </div>
                </div>
              )}
              
              {/* Kleinunternehmerregelung for Germany & Austria */}
              {(selectedCountry === 'DE' || selectedCountry === 'AT' || selectedCountry === 'CH') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="mr-2">{selectedCountry === 'DE' ? '🇩🇪' : selectedCountry === 'AT' ? '🇦🇹' : '🇨🇭'}</span>
                      Kleinunternehmerregelung
                    </label>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${user?.is_kleinunternehmer ? 'text-green-600' : 'text-gray-500'}`}>
                        {user?.is_kleinunternehmer ? 'Aktiv' : 'Inaktiv'}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const newValue = !user?.is_kleinunternehmer;
                            await api.put('/user/kleinunternehmer', { 
                              is_kleinunternehmer: newValue,
                              kleinunternehmer_country: selectedCountry 
                            });
                            // Refresh user data
                            window.location.reload();
                            toast.success(newValue ? 'Kleinunternehmerregelung aktiviert' : 'Kleinunternehmerregelung deaktiviert');
                          } catch (error) {
                            console.error('Error updating Kleinunternehmer status:', error);
                            toast.error('Fehler beim Aktualisieren');
                          }
                        }}
                        className={`px-3 py-1 ${
                          user?.is_kleinunternehmer 
                            ? 'bg-gray-500 hover:bg-gray-600' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        } text-white text-xs font-medium rounded transition-colors`}
                      >
                        {user?.is_kleinunternehmer ? 'Deaktivieren' : 'Aktivieren'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-700 space-y-2">
                    {selectedCountry === 'DE' && (
                      <>
                        <p className="font-semibold">
                          Kleinunternehmerregelung (§ 19 UStG)
                        </p>
                        <p>
                          Als Kleinunternehmer müssen Sie keine Umsatzsteuer auf Ihren Rechnungen ausweisen.
                        </p>
                        <div className="bg-white p-3 rounded border border-blue-300">
                          <p className="font-semibold mb-1">Voraussetzungen:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Umsatz im Vorjahr ≤ 22.000 €</li>
                            <li>Voraussichtlicher Umsatz im laufenden Jahr ≤ 50.000 €</li>
                          </ul>
                        </div>
                        <p className="text-blue-700 font-semibold">
                          ℹ️ Hinweis: Sie können dann auch keine Vorsteuer geltend machen!
                        </p>
                      </>
                    )}
                    {selectedCountry === 'AT' && (
                      <>
                        <p className="font-semibold">
                          Kleinunternehmerregelung (§ 6 Abs. 1 Z 27 UStG)
                        </p>
                        <p>
                          Als Kleinunternehmer müssen Sie keine Umsatzsteuer auf Ihren Rechnungen ausweisen.
                        </p>
                        <div className="bg-white p-3 rounded border border-blue-300">
                          <p className="font-semibold mb-1">Voraussetzungen:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Jahresumsatz ≤ 35.000 €</li>
                          </ul>
                        </div>
                        <p className="text-blue-700 font-semibold">
                          ℹ️ Hinweis: Sie können dann auch keine Vorsteuer geltend machen!
                        </p>
                      </>
                    )}
                    {selectedCountry === 'CH' && (
                      <>
                        <p className="font-semibold">
                          Kleinunternehmerregelung (Art. 10 Abs. 2 MWSTG)
                        </p>
                        <p>
                          In der Schweiz sind Sie von der MWST-Pflicht befreit, wenn Ihr Jahresumsatz unter CHF 100'000 liegt.
                        </p>
                        <div className="bg-white p-3 rounded border border-blue-300">
                          <p className="font-semibold mb-1">Voraussetzungen:</p>
                          <ul className="list-disc list-inside space-y-1">
                            <li>Jahresumsatz {'<'} 100'000 CHF</li>
                          </ul>
                        </div>
                        <p className="text-orange-600 font-semibold">
                          ⚠️ Achtung: Unterschiedliche Regelung in der Schweiz - bitte Steuerberater konsultieren!
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Warning for OTHER country selection */}
              {selectedCountry === 'OTHER' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold mb-1">
                        {t("settings.otherCountryTitle", "Limited Functionality")}
                      </p>
                      <p className="mb-2">
                        {t("settings.otherCountryWarning", "No specific tax calculations available for your country.")}
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>{t("settings.otherCountryLimit1", "Keine automatische Steuervorberechnung")}</li>
                        <li>{t("settings.otherCountryLimit2", "Generische Mehrwertsteuersätze")}</li>
                        <li>{t("settings.otherCountryLimit3", "You must manually enter tax rates in invoices")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Country Change Warning Dialog */}
              <AlertDialog open={countryChangeDialogOpen} onOpenChange={setCountryChangeDialogOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-orange-600 flex items-center gap-2">
                      <span className="text-2xl">⚠️</span>
                      Warnung: Länderwechsel
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3 text-left">
                      <p className="font-semibold text-gray-900">
                        Ein Wechsel des Landes kann zu Inkonsistenzen führen.
                      </p>
                      <div className="bg-orange-50 border border-orange-300 p-3 rounded">
                        <p className="font-bold text-orange-900 mb-2">Auswirkungen:</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-orange-900">
                          <li><strong>Bestehende Rechnungen</strong> behalten ihre ursprüngliche Währung (keine Konvertierung)</li>
                          <li><strong>Neue Rechnungen</strong> werden in der neuen Währung erstellt</li>
                          <li><strong>Steuersätze</strong> werden auf das neue Land angepasst</li>
                          <li><strong>Ausgaben</strong> werden konvertiert, was zu Rundungsfehlern führen kann</li>
                        </ul>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                        <p className="font-semibold text-blue-900 mb-2">💡 Unsere Empfehlung:</p>
                        <p className="text-sm text-blue-800">
                          Wenn Sie dauerhaft in ein anderes Land wechseln, empfehlen wir ein <strong>neues Konto</strong> zu erstellen, um Ihre Buchhaltung sauber zu trennen.
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        Möchten Sie das Land wirklich wechseln?
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => {
                      setPendingCountry('');
                      setCountryChangeDialogOpen(false);
                    }}>
                      Abbrechen
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        setSelectedCountry(pendingCountry);
                        setPendingCountry('');
                        setCountryChangeDialogOpen(false);
                      }}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      Ja, Land wechseln
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving || selectedCountry === countryInfo.country_code}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {saving ? t("common.saving", "Saving...") : t("common.save", "Save")}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  {t("settings.cancel", "Abbrechen")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("settings.country")}:</span>
                <span className="text-gray-700">{countryInfo.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium">{t("settings.currency")}:</span>
                <span className="text-gray-700">{countryInfo.currency}</span>
              </div>
              <div>
                <span className="font-medium">{t("settings.vatRates")}:</span>
                <ul className="mt-2 space-y-1 ml-4">
                  {countryInfo.vat_rates?.map((rate, index) => (
                    <li key={index} className="text-sm text-gray-600">
                      {rate.label}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Info box for OTHER country */}
              {countryInfo.country_code === 'OTHER' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">
                        {t("settings.otherCountryInfoTitle", "Hinweis zur Steuerberechnung")}
                      </p>
                      <p>
                        {t("settings.otherCountryInfoText", "Automatic tax calculations are not available for your country. You can use generic VAT rates or enter your own values in invoices.")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-4">
                {t("settings.countryNote")}
              </p>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
